// src/pages/HomePage.js
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useScroll, useSpring, useInView } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import WalletModal from '../components/WalletModal/WalletModal';

// Detailed robot-themed DappFexez logo (shield + robot face)
const QuadricfLogo = ({ className = "h-9 w-9" }) => (
  <svg
    className={className}
    viewBox="0 0 64 64"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="DappFexez robot logo"
  >
    <defs>
      <linearGradient id="gold-sheen" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f5d76e" />
        <stop offset="50%" stopColor="#f59e0b" />
        <stop offset="100%" stopColor="#eab308" />
      </linearGradient>
      <linearGradient id="gold-rim" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#facc15" />
        <stop offset="100%" stopColor="#fb923c" />
      </linearGradient>
    </defs>
    {/* Shield base */}
    <path d="M32 6 L52 14 V32 c0 11 -8 18 -20 22 C20 50 12 43 12 32 V14 Z" fill="#111318" stroke="url(#gold-rim)" strokeWidth="1.5" />
    {/* Inner plate */}
    <path d="M32 12 l14 6 v12 c0 8 -6 13 -14 16 c-8 -3 -14 -8 -14 -16 V18 Z" fill="rgba(250, 204, 21, 0.08)" stroke="#eab308" strokeOpacity="0.3" strokeWidth="1" />
    {/* Robot head */}
    <rect x="22" y="22" width="20" height="14" rx="3" ry="3" fill="url(#gold-sheen)" stroke="#9a7b16" strokeWidth="0.8" />
    {/* Antennas */}
    <circle cx="21" cy="20" r="2" fill="#eab308" />
    <circle cx="43" cy="20" r="2" fill="#eab308" />
    <path d="M21 20 L24 22 M43 20 L40 22" stroke="#f59e0b" strokeWidth="1" />
    {/* Eyes */}
    <circle cx="28" cy="28" r="2.2" fill="#0a0f23" />
    <circle cx="36" cy="28" r="2.2" fill="#0a0f23" />
    <circle cx="28" cy="28" r="1" fill="#fef9c3" />
    <circle cx="36" cy="28" r="1" fill="#fef9c3" />
    {/* Mouth grille */}
    <rect x="26" y="31.5" width="12" height="2.8" rx="1.2" fill="#0a0f23" stroke="#facc15" strokeWidth="0.6" />
    <path d="M29 31.5 v2.8 M32 31.5 v2.8 M35 31.5 v2.8" stroke="#facc15" strokeWidth="0.6" />
    {/* Chin plate */}
    <rect x="29" y="35.5" width="6" height="2" rx="0.7" fill="#eab308" opacity="0.8" />
  </svg>
);

// Self-contained rotating phrase that doesn't re-render the parent tree
const RotatingPhrase = React.memo(function RotatingPhrase() {
  const phrases = React.useRef([
    'instant migration recovery',
    'trustless token bridging',
    'seamless claim resolution',
    'smart swap diagnostics',
    'NFT rescue & registry sync',
    'multisig wallet validation'
  ]).current;
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % phrases.length), 2600);
    return () => clearInterval(id);
  }, [phrases.length]);
  return (
    <span className="inline-block font-semibold text-[#facc15]">
      <AnimatePresence mode="wait">
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.45 }}
          className="whitespace-nowrap inline-block min-w-[12ch] text-center"
        >
          {phrases[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
});

const HomePage = ({ openConnectOnLoad = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navRef = useRef(null);
  const spotlightRef = useRef(null);
  const navigate = useNavigate();
  // Use framer-motion scroll progress to avoid React state re-renders on scroll
  const { scrollYProgress } = useScroll();
  const scrollX = useSpring(scrollYProgress, { stiffness: 120, damping: 20, mass: 0.4 });
  

  // No React state updates on scroll; progress handled by scrollYProgress

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = spotlightRef.current;
      if (!r) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      r.style.setProperty('--spot-x', `${x}px`);
      r.style.setProperty('--spot-y', `${y}px`);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  useEffect(() => {
    if (openConnectOnLoad) {
      setIsModalOpen(true);
      // Ensure scroll at top when this route is used
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [openConnectOnLoad]);

  

  // Minimal crypto icons (monochrome gold) for the carousel
  const CryptoIcon = ({ symbol }) => {
    switch (symbol) {
      case 'BTC':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <circle cx="12" cy="12" r="10" fill="#facc15" />
            <path d="M10 7h3.5a2.5 2.5 0 010 5H9m1 0h3a2.5 2.5 0 010 5H10" stroke="#0a0f23" strokeWidth="1.8" fill="none" strokeLinecap="round" />
          </svg>
        );
      case 'ETH':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <circle cx="12" cy="12" r="10" fill="#f59e0b" />
            <path d="M12 5l4 7-4 2-4-2 4-7zm0 14l-4-6 4 2 4-2-4 6z" fill="#0a0f23" />
          </svg>
        );
      case 'SOL':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <circle cx="12" cy="12" r="10" fill="#fb923c" />
            <path d="M7 9h10l-2 2H5l2-2zm0 6h10l-2 2H5l2-2zm2-3h10l-2 2H7l2-2z" fill="#0a0f23" />
          </svg>
        );
      case 'BNB':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <circle cx="12" cy="12" r="10" fill="#fde68a" />
            <path d="M12 6l2 2-2 2-2-2 2-2zm0 8l2 2-2 2-2-2 2-2zm4-4l2 2-2 2-2-2 2-2zm-8 0l2 2-2 2-2-2 2-2z" fill="#0a0f23" />
          </svg>
        );
      case 'ADA':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
            <circle cx="12" cy="12" r="10" fill="#fbbf24" />
            <g fill="#0a0f23">
              <circle cx="12" cy="12" r="2" />
              <circle cx="7.5" cy="12" r="1.2" />
              <circle cx="16.5" cy="12" r="1.2" />
              <circle cx="10" cy="8.5" r="1" />
              <circle cx="14" cy="8.5" r="1" />
              <circle cx="10" cy="15.5" r="1" />
              <circle cx="14" cy="15.5" r="1" />
            </g>
          </svg>
        );
      default:
        return null;
    }
  };

  const cryptoItems = [
    { symbol: 'BTC', name: 'Bitcoin' },
    { symbol: 'ETH', name: 'Ethereum' },
    { symbol: 'SOL', name: 'Solana' },
    { symbol: 'BNB', name: 'BNB' },
    { symbol: 'ADA', name: 'Cardano' },
  ];

  const BlockchainIllustration = () => (
    <div className="mb-16">
      <motion.svg 
        viewBox="0 0 400 300" 
        className="w-full max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        <defs>
          <linearGradient id="cube-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6"/>
            <stop offset="100%" stopColor="#1d4ed8"/>
          </linearGradient>
        </defs>
        
        {/* Main cubes */}
        <g>
          <motion.path 
            d="M160,120 L200,100 L240,120 L200,140 Z" 
            fill="url(#cube-gradient)" 
            opacity="0.9"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.9 }}
            transition={{ duration: 1, delay: 0.8 }}
          />
          <motion.path 
            d="M200,100 L240,120 L240,160 L200,140 Z" 
            fill="url(#cube-gradient)" 
            opacity="0.8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.8 }}
            transition={{ duration: 1, delay: 1 }}
          />
          <motion.path 
            d="M160,120 L200,140 L200,180 L160,160 Z" 
            fill="url(#cube-gradient)" 
            opacity="0.7"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ duration: 1, delay: 1.2 }}
          />
        </g>
        
        {/* Connection lines */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ duration: 1, delay: 1.4 }}
        >
          <path d="M180,130 L180,150" stroke="#3b82f6" strokeWidth="1" strokeDasharray="3,3"/>
          <path d="M220,130 L220,150" stroke="#3b82f6" strokeWidth="1" strokeDasharray="3,3"/>
        </motion.g>
      </motion.svg>
    </div>
  );

  // Animated section wrapper component
  const AnimatedSection = ({ children, className = '', delay = 0 }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.2 });
    
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 40 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
        transition={{ duration: 0.6, delay, ease: 'easeOut' }}
        className={className}
      >
        {children}
      </motion.div>
    );
  };

  // Handler to navigate to troubleshooter page
  const handleGoToTroubleshooter = () => {
    navigate('/troubleshooter');
  };

  // Header navigation items mapped to page sections
  const navItems = [
    { label: 'Troubleshooter', href: '/troubleshooter' },
    { label: 'Expertise', href: '#expertise' },
    { label: 'NFTs', href: '#nft-services' },
    { label: 'FaQ', href: '#faq' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <div className="min-h-screen bg-[#1b130a] text-white relative overflow-hidden font-sans pb-12 md:pb-8">
      {/* Aurora animated background overlay (purely visual) */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -inset-40 z-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        style={{
          background: 'radial-gradient(60% 60% at 20% 20%, rgba(59,130,246,0.15) 0%, rgba(59,130,246,0) 60%), radial-gradient(50% 50% at 80% 30%, rgba(99,102,241,0.15) 0%, rgba(99,102,241,0) 60%), radial-gradient(40% 40% at 50% 80%, rgba(56,189,248,0.12) 0%, rgba(56,189,248,0) 60%)'
        }}
      />
      <div className="fixed inset-0">
        {/* Horizontal scanning lines */}
        <div className="hidden sm:block">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={`scan-${i}`}
            className="absolute h-[2px] w-full bg-gradient-to-r from-transparent via-[rgba(146,98,7,0.35)] to-transparent"
            animate={{
              x: ['-100%', '100%'],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 3,
              delay: i * 0.4,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              top: `${15 + i * 10}%`,
            }}
          />
        ))}
        </div>

        {/* Additional horizontal tech lines */}
        <div className="hidden sm:block">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={`tech-${i}`}
            className="absolute h-[1px] w-[200px] bg-gradient-to-r from-[rgba(146,98,7,0.25)] to-transparent"
            animate={{
              x: ['0%', '100%'],
              opacity: [0.5, 0, 0.5],
            }}
            transition={{
              duration: 5,
              delay: i * 0.3,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              top: `${5 + i * 6}%`,
              left: `${(i % 3) * 30}%`,
            }}
          />
        ))}
        </div>

        {/* Tech grid with dots */}
        <div className="hidden sm:block absolute inset-0 opacity-[0.1]">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={`dot-${i}`}
              className="absolute h-1 w-1 bg-[rgba(146,98,7,0.9)] rounded-full"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 2,
                delay: i * 0.2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{
                left: `${(i % 5) * 25}%`,
                top: `${Math.floor(i / 5) * 25}%`,
              }}
            />
          ))}
        </div>

        {/* Animated circuit paths */}
        <svg className="hidden sm:block absolute inset-0 w-full h-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
          <motion.path
            d="M0 100 Q 100 100 200 200 T 400 300"
            stroke="url(#circuit-gradient)"
            strokeWidth="2"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <defs>
            <linearGradient id="circuit-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#a16207" stopOpacity="0" />
              <stop offset="50%" stopColor="#92400e" stopOpacity="1" />
              <stop offset="100%" stopColor="#b45309" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* Matrix-style falling characters */}
        <div className="hidden sm:block absolute inset-0 opacity-[0.05]">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-px h-32 bg-gradient-to-b from-transparent via-[rgba(146,98,7,0.8)] to-transparent"
              animate={{
                y: ['-100%', '100vh'],
              }}
              transition={{
                duration: 10,
                delay: i * 0.8,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{
                left: `${i * 10}%`,
              }}
            />
          ))}
        </div>

        {/* Hexagon grid pattern */}
        <div 
          className="hidden sm:block absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5L55 20V40L30 55L5 40V20L30 5Z' stroke='%2392400e' fill='none' stroke-width='0.5'/%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}
        />

        {/* Keep existing background elements */}
        <div className="hidden sm:block absolute inset-0 opacity-[0.07]">
          <div className="absolute h-[200%] w-full transform -rotate-45">
            {[...Array(10)].map((_, i) => (
              <motion.div
                key={i}
                className="h-24 bg-gradient-to-r from-amber-700/10 via-transparent to-transparent"
                animate={{
                  x: ['-10%', '10%'],
                }}
                transition={{
                  duration: 8,
                  delay: i * 0.5,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut"
                }}
                style={{
                  transform: `translateY(${i * 96}px)`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Enhanced glow effects */}
        <div className="hidden sm:block absolute inset-0">
          <motion.div 
            className="absolute top-[20%] left-[10%] w-96 h-96 bg-[rgba(146,98,7,0.22)] rounded-full filter blur-[128px]"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div 
            className="absolute bottom-[20%] right-[10%] w-96 h-96 bg-[rgba(180,83,9,0.18)] rounded-full filter blur-[128px]"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        {/* Digital Binary Rain Effect */}
        <div className="hidden sm:block">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={`binary-${i}`}
            className="absolute text-xs text-[rgba(146,98,7,0.4)] whitespace-nowrap overflow-hidden"
            style={{
              left: `${i * 7}%`,
              top: '-20px',
              writingMode: 'vertical-rl',
            }}
            animate={{
              y: ['0%', '100%'],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "linear",
              delay: Math.random() * 2,
            }}
          >
            {[...Array(30)].map(() => Math.round(Math.random())).join('')}
          </motion.div>
        ))}
        </div>

        {/* Animated Hexagonal Grid */}
        <div className="hidden sm:block absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={`hex-${i}`}
              className="absolute w-32 h-32 border border-[rgba(146,98,7,0.2)]"
              style={{
                left: `${(i % 3) * 33}%`,
                top: `${Math.floor(i / 3) * 50}%`,
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.5,
              }}
            />
          ))}
        </div>

        {/* Animated Circuit Board Patterns */}
        <svg className="hidden sm:block absolute inset-0 w-full h-full opacity-[0.07]">
          <pattern id="circuit-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
            <motion.path
              d="M10 10 L90 10 M50 10 L50 90 M10 50 L90 50 M10 90 L90 90"
              stroke="url(#circuit-gradient-2)"
              strokeWidth="0.5"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            <circle cx="50" cy="50" r="3" fill="#a16207" fillOpacity="0.26" />
          </pattern>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#circuit-pattern)" />
          <defs>
            <linearGradient id="circuit-gradient-2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a16207" stopOpacity="0.4" />
              <stop offset="50%" stopColor="#92400e" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#b45309" stopOpacity="0.4" />
            </linearGradient>
          </defs>
        </svg>

        {/* Pulsing Radar Circles */}
        <div className="hidden sm:block">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`radar-${i}`}
            className="absolute rounded-full border border-[rgba(146,98,7,0.2)]"
            style={{
              left: '50%',
              top: '50%',
              width: `${(i + 1) * 200}px`,
              height: `${(i + 1) * 200}px`,
              transform: 'translate(-50%, -50%)',
            }}
            animate={{
              scale: [1, 1.5],
              opacity: [0.2, 0],
            }}
            transition={{
              duration: 3,
              delay: i * 0.8,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
        </div>

        {/* Tech Grid Lines */}
        <div className="hidden sm:block absolute inset-0" 
             style={{
               backgroundImage: `
                 linear-gradient(90deg, rgba(146, 98, 7, 0.08) 1px, transparent 1px),
                 linear-gradient(0deg, rgba(146, 98, 7, 0.08) 1px, transparent 1px)
               `,
               backgroundSize: '20px 20px'
             }}
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1b130a]/90 via-transparent to-[#1b130a]/90" />
      </div>

      {/* Navigation Bar */}
      <motion.nav 
        ref={navRef}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-30 border-b border-[#4a2f17]/40 bg-[#1a1209]/70 backdrop-blur-xl"
      >
        {/* Scroll progress bar */}
        <div className="absolute top-0 left-0 h-[2px] w-full bg-transparent overflow-hidden">
          <motion.div 
            className="h-full w-full bg-gradient-to-r from-[#a16207] via-[#92400e] to-[#b45309] origin-left"
            style={{ scaleX: scrollX }}
          />
        </div>

        {/* Spotlight cursor */}
        <div 
          ref={spotlightRef}
          className="hidden md:block absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(120px 120px at var(--spot-x, -200px) var(--spot-y, -200px), rgba(146,98,7,0.18), transparent 60%)'
          }}
        />

        <div className="max-w-6xl mx-auto px-4 md:px-8 py-2 md:py-3 flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3">
            <QuadricfLogo className="h-8 w-8 sm:h-9 sm:w-9" />
              <span className="text-base sm:text-lg md:text-xl font-semibold bg-gradient-to-r from-white to-gray-300 
                           bg-clip-text text-transparent whitespace-nowrap truncate">
              DappFexez
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            {/* Desktop Header nav items */}
            <div className="hidden lg:flex items-center gap-4">
              {navItems.map((item) => (
                item.href.startsWith('#') ? (
                  <motion.div 
                    key={item.href}
                    whileHover={{ scale: 1.05 }} 
                    whileTap={{ scale: 0.95 }}
                  >
                    <button 
                      onClick={handleGoToTroubleshooter}
                      className="text-sm text-gray-300 hover:text-white transition-colors"
                    >
                      {item.label}
                    </button>
                  </motion.div>
                ) : (
                  <motion.div key={item.href} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link to={item.href} className="text-sm text-gray-300 hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  </motion.div>
                )
              ))}
            </div>
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-gray-300 hover:text-white transition-colors"
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X size={24} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu size={24} />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
            {/* Primary CTA with magnetic hover */}
            <motion.button
              whileHover={{ y: -1, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGoToTroubleshooter}
              className="relative bg-gradient-to-r from-[#92400e] to-[#b45309] text-white px-3 sm:px-4 md:px-5 py-2 rounded-lg 
                       hover:from-[#a16207] hover:to-[#c2410c] transition-colors duration-300 font-semibold text-sm sm:text-base
                       shadow-[0_10px_30px_rgba(146,98,7,0.28)]"
            >
              <span className="hidden sm:inline">Connect Wallet</span>
              <span className="sm:hidden">Connect</span>
              <span className="absolute inset-0 rounded-lg ring-1 ring-black/10"></span>
            </motion.button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden border-t border-[#4a2f17]/40 bg-[#1a1209]/95 backdrop-blur-sm"
            >
              <div className="px-4 py-4 space-y-3">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {item.href.startsWith('#') ? (
                      <button 
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          handleGoToTroubleshooter();
                        }}
                        className="block text-sm text-gray-300 hover:text-white py-2 transition-colors w-full text-left"
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link 
                        to={item.href} 
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block text-sm text-gray-300 hover:text-white py-2 transition-colors"
                      >
                        {item.label}
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Add spacing after menu */}
      <div className="h-4 md:h-8"></div>

      <div className="relative z-10 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Icon moved to the very top of the page */}
          <div className="flex items-center justify-center mb-6 md:mb-8">
            <BlockchainIllustration />
          </div>
          {/* Hero Section (top banner) */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 sm:mb-6 bg-gradient-to-r from-[#fde68a] via-[#a16207] to-white bg-clip-text text-transparent">
              DappFexez — All Chains Resolver
            </h1>
            <p className="text-base sm:text-lg text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto">
              DappFexez is a protocol for autonomous, cross‑chain remediation — engineered to fix what slows you down and safeguard what moves you forward. Experience{' '}
              <RotatingPhrase />{' '}across the wallets and networks you already use — continuously, securely, and on your terms.
            </p>
              <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGoToTroubleshooter}
                className="bg-gradient-to-r from-[#92400e] to-[#b45309] text-white w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg hover:from-[#a16207] hover:to-[#c2410c] 
                         transition-colors duration-300 font-semibold shadow-lg shadow-[rgba(146,98,7,0.35)]"
            >
              Get Started
            </motion.button>
          </motion.div>

          {/* Trusted by Thousands section */}
          <AnimatedSection id="trusted" className="mb-8 md:mb-10" delay={0.2}>
            <div className="text-center mb-4 px-2">
              <motion.h2 
                className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                Trusted by Thousands Worldwide
              </motion.h2>
                <motion.p 
                className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Secure, reliable, and designed for real‑world multi‑chain support with modern protections, best‑practice flows, and continuous improvements. DappFexez scales from individual wallets to institutional teams, helping you diagnose issues faster and execute remediations confidently.
              </motion.p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent">
              <motion.img 
                src="/hpassets/hero.jpg" 
                alt="Trusted" 
                className="block w-full h-[280px] sm:h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                onError={(e) => { e.target.src = '/hpassets/hero.jpg'; }}
              />
            </div>
            <div className="text-center mt-4">
              <motion.button 
                onClick={handleGoToTroubleshooter} 
                className="px-4 sm:px-5 py-2 sm:py-3 rounded-md bg-gradient-to-r from-[#92400e] to-[#b45309] text-white font-semibold hover:from-[#a16207] hover:to-[#c2410c] text-sm sm:text-base"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Resolve Issues
              </motion.button>
            </div>
          </AnimatedSection>

            {/* Crypto Carousel (moved up below the first call-to-action) */}
            <div className="mt-6">
              <div className="relative overflow-hidden rounded-xl border border-[#3a2a06]/30 bg-[#0A0F23]/60 backdrop-blur-sm">
                <div className="pointer-events-none absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-[#070B1C] to-transparent" />
                <div className="pointer-events-none absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-[#070B1C] to-transparent" />
                <motion.div
                  className="flex gap-8 py-4 px-4"
                  animate={{ x: ['0%', '-50%'] }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                >
                  {[...cryptoItems, ...cryptoItems].map((c, i) => (
                    <div key={`${c.symbol}-top-${i}`} className="flex items-center gap-3 min-w-max">
                      <div className="h-8 w-8 rounded-full flex items-center justify-center bg-[#1a1f2e] ring-1 ring-[#3a2a06]/40">
                        <CryptoIcon symbol={c.symbol} />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-white">{c.symbol}</span>
                        <span className="text-xs text-gray-400">{c.name}</span>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>


          {/* Reusable image section helper */}
          {/* Wallet Troubleshooter */}
          <AnimatedSection id="troubleshooter" className="mb-8 md:mb-10" delay={0.1}>
            <div className="text-center mb-4 px-2">
              <motion.h2 
                className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                Wallet Troubleshooter
              </motion.h2>
              <motion.p 
                className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Diagnostics and guided fixes for common wallet issues, including missing balances, stuck transactions, incorrect nonces, and risky approvals. Our guided flows explain what's happening and why, before proposing a safe, reversible next step.
              </motion.p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent">
              <motion.img 
                src="/hpassets/wallet-troubleshooter.jpg" 
                alt="Wallet Troubleshooter" 
                className="block w-full h-[280px] sm:h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                onError={(e) => { e.target.src = '/hpassets/hero.jpg'; }}
              />
            </div>
            <div className="text-center mt-4">
              <motion.button 
                onClick={handleGoToTroubleshooter} 
                className="px-4 sm:px-5 py-2 sm:py-3 rounded-md bg-gradient-to-r from-[#92400e] to-[#b45309] text-white font-semibold hover:from-[#a16207] hover:to-[#c2410c] text-sm sm:text-base"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started
              </motion.button>
            </div>
          </AnimatedSection>

          {/* Take Full Control of Your Crypto */}
          <AnimatedSection id="control" className="mb-8 md:mb-10" delay={0.1}>
            <div className="text-center mb-4 px-2">
              <motion.h2 
                className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                Take Full Control of Your Crypto
              </motion.h2>
              <motion.p 
                className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Insights and resolutions for permissions, approvals, policy locks, and address safety so you can transact with confidence. Easily review allowances, revoke problematic approvals, and verify contract integrity across chains.
              </motion.p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent">
              <motion.img 
                src="/hpassets/control-crypto.jpg" 
                alt="Control Crypto" 
                className="block w-full h-[280px] sm:h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                onError={(e) => { e.target.src = '/hpassets/hero.jpg'; }}
              />
            </div>
            <div className="text-center mt-4">
              <motion.button 
                onClick={handleGoToTroubleshooter} 
                className="px-4 sm:px-5 py-2 sm:py-3 rounded-md bg-gradient-to-r from-[#92400e] to-[#b45309] text-white font-semibold hover:from-[#a16207] hover:to-[#c2410c] text-sm sm:text-base"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started
              </motion.button>
            </div>
          </AnimatedSection>

          {/* Our Troubleshooting Expertise */}
          <AnimatedSection id="expertise" className="mb-8 md:mb-10" delay={0.1}>
            <div className="text-center mb-4 px-2">
              <motion.h2 
                className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                Our Troubleshooting Expertise
              </motion.h2>
              <motion.p 
                className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                From swaps to bridges, we help you identify and fix cross‑chain issues with repeatable workflows and clear guidance. We surface the root cause—RPC, gas, liquidity, or policy—so you can resolve with minimal retries and fees.
              </motion.p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent">
              <motion.img 
                src="/hpassets/troubleshooting-expertise.jpg" 
                alt="Troubleshooting" 
                className="block w-full h-[280px] sm:h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                onError={(e) => { e.target.src = '/hpassets/hero.jpg'; }}
              />
            </div>
            <div className="text-center mt-4">
              <motion.button 
                onClick={handleGoToTroubleshooter} 
                className="px-4 sm:px-5 py-2 sm:py-3 rounded-md bg-gradient-to-r from-[#92400e] to-[#b45309] text-white font-semibold hover:from-[#a16207] hover:to-[#c2410c] text-sm sm:text-base"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started
              </motion.button>
            </div>
          </AnimatedSection>

          {/* NFT Troubleshooting Services */}
          <AnimatedSection id="nft-services" className="mb-8 md:mb-10" delay={0.1}>
            <div className="text-center mb-4 px-2">
              <motion.h2 
                className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                NFT Troubleshooting Services
              </motion.h2>
              <motion.p 
                className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Restore metadata visibility, refresh token URIs, sync marketplace listings, and resolve transfer hiccups across chains and wallets. DappFexez keeps creators, collectors, and marketplaces in sync with minimal manual effort.
              </motion.p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent">
              <motion.img 
                src="/hpassets/nft-services.jpg" 
                alt="NFT Services" 
                className="block w-full h-[280px] sm:h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                onError={(e) => { e.target.src = '/hpassets/hero.jpg'; }}
              />
            </div>
            <div className="text-center mt-4">
              <motion.button 
                onClick={handleGoToTroubleshooter} 
                className="px-4 sm:px-5 py-2 sm:py-3 rounded-md bg-gradient-to-r from-[#92400e] to-[#b45309] text-white font-semibold hover:from-[#a16207] hover:to-[#c2410c] text-sm sm:text-base"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started
              </motion.button>
            </div>
          </AnimatedSection>

          {/* Why Choose DappFexez for NFTs? */}
          <AnimatedSection id="why-nft" className="mb-8 md:mb-10" delay={0.1}>
            <div className="text-center mb-4 px-2">
              <motion.h2 
                className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                Why Choose DappFexez for NFTs?
              </motion.h2>
              <motion.p 
                className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Purpose‑built workflows for metadata, indexing, and consistent marketplace display of your assets. We prioritize provenance, on‑chain truth, and practical UX so your collections look right everywhere.
              </motion.p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent">
              <motion.img 
                src="/hpassets/why-choose-nft.jpg" 
                alt="Why Choose NFTs" 
                className="block w-full h-[280px] sm:h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                onError={(e) => { e.target.src = '/hpassets/hero.jpg'; }}
              />
            </div>
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 text-center text-sm text-gray-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.div 
                className="rounded-lg border border-[#3a2a06]/30 p-3 hover:border-[#92400e] transition-colors"
                whileHover={{ scale: 1.05 }}
              >
                Cross‑chain indexing
              </motion.div>
              <motion.div 
                className="rounded-lg border border-[#3a2a06]/30 p-3 hover:border-[#92400e] transition-colors"
                whileHover={{ scale: 1.05 }}
              >
                Marketplace sync
              </motion.div>
              <motion.div 
                className="rounded-lg border border-[#3a2a06]/30 p-3 hover:border-[#92400e] transition-colors"
                whileHover={{ scale: 1.05 }}
              >
                On‑chain provenance
              </motion.div>
            </motion.div>
          </AnimatedSection>

          {/* Testimonials */}
          <section id="testimonials" className="mb-8 md:mb-10">
            <div className="text-center mb-4">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">What Our Users Say</h2>
              <p className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3">Real feedback from users who resolved critical multi‑chain issues with DappFexez. Faster resolution, safer execution, and fewer retries are the reasons users stick with our guided flows.</p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent">
              <motion.img 
                src="/hpassets/hero.jpg" 
                alt="Testimonial" 
                className="block w-full h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                onError={(e) => { e.target.src = '/hpassets/hero.jpg'; }}
              />
            </div>
          </section>

          {/* About the Platform */}
          <section id="about-platform" className="mb-8 md:mb-10">
            <div className="text-center mb-4">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">About the Platform</h2>
              <p className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3">Observability, automation, and guided remediation built for every chain and wallet you already use. DappFexez integrates seamlessly into your existing workflow—no new seed phrases, no migrations, just outcomes.</p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent">
              <motion.img 
                src="/hpassets/about-platform.png" 
                alt="About Platform" 
                className="block w-full h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                onError={(e) => { e.target.src = '/hpassets/hero.jpg'; }}
              />
            </div>
          </section>

          {/* Why Choose DappFexez? */}
          <section id="why-choose" className="mb-8 md:mb-10">
            <div className="text-center mb-4">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">Why Choose DappFexez?</h2>
              <p className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3">Purpose‑built for speed, safety, and multi‑chain compatibility with a focus on user trust and clarity. Everything is explained in plain English, with reversible actions whenever possible.</p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent">
              <motion.img 
                src="/hpassets/why-choose.png" 
                alt="Why Choose" 
                className="block w-screen max-w-none relative left-1/2 -translate-x-1/2 h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              />
            </div>
          </section>

          {/* Roadmap */}
          <section id="roadmap" className="mb-8 md:mb-10">
            <div className="text-center mb-4">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">DappFexez Roadmap</h2>
              <p className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3">Continuous delivery of new integrations and automated resolvers to reduce friction over time. Expect new chain support, smarter diagnostics, and safer default paths—shipped regularly.</p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent">
              <motion.img 
                src="/hpassets/troubleshooting-expertise.jpg" 
                alt="Roadmap" 
                className="block w-full h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                onError={(e) => { e.target.src = '/hpassets/hero.jpg'; }}
              />
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="mb-8 md:mb-10">
            <div className="text-center mb-4">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">FAQ</h2>
              <p className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3">Answers to common questions about setup, security, and troubleshooting across multiple chains and wallets.</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              <div className="rounded-lg border border-[#3a2a06]/30 bg-[#0A0F23]/60 p-4">
                <p className="text-sm text-gray-300"><span className="text-[#facc15] font-semibold">How do I start?</span> Click any action above and connect your wallet to begin guided remediation.</p>
              </div>
              <div className="rounded-lg border border-[#3a2a06]/30 bg-[#0A0F23]/60 p-4">
              <p className="text-sm text-gray-300"><span className="text-[#facc15] font-semibold">Is it secure?</span> DappFexez follows best‑practice client flows; we never custody your assets.</p>
              </div>
              <div className="rounded-lg border border-[#3a2a06]/30 bg-[#0A0F23]/60 p-4">
                <p className="text-sm text-gray-300"><span className="text-[#facc15] font-semibold">Which chains are supported?</span> Major EVM, Solana, and more—expanding continuously.</p>
              </div>
            </div>
          </section>

          {/* About Us */}
          <section id="about-us" className="mb-8 md:mb-10">
            <div className="text-center mb-4">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">About Us</h2>
              <p className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3">We’re a distributed team building safer cross‑chain experiences for everyone. Our mission is simple: remove friction and uncertainty so people can just use crypto—safely.</p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent">
              <motion.img 
                src="/hpassets/wallet-troubleshooter.jpg" 
                alt="About Us" 
                className="block w-screen max-w-none relative left-1/2 -translate-x-1/2 h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              />
            </div>
          </section>

          {/* Contact Us */}
          <section id="contact" className="mb-8 md:mb-10">
            <div className="text-center mb-4">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">Contact Us</h2>
              <p className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto px-3">Need help right now? Connect and we’ll guide you step‑by‑step with clear, safe actions. Most issues resolve within minutes once the root cause is identified.</p>
            </div>
            <div className="overflow-hidden rounded-none border-0 bg-transparent mb-4">
              <motion.img 
                src="/hpassets/hero.jpg" 
                alt="Contact" 
                className="block w-full h-[360px] md:h-[420px] lg:h-[500px] object-cover"
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                onError={(e) => { e.target.src = '/hpassets/hero.jpg'; }}
              />
            </div>
            <div className="text-center">
              <motion.button 
                onClick={handleGoToTroubleshooter} 
                className="px-5 py-3 rounded-md bg-gradient-to-r from-[#92400e] to-[#b45309] text-white font-semibold hover:from-[#a16207] hover:to-[#c2410c]"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started
              </motion.button>
            </div>
          </section>

          {/* Crypto Carousel moved to the top section */}

          {/* Optional illustration can remain below marketing sections */}
          <BlockchainIllustration />
        </div>
      </div>

      <WalletModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#3a2a06]/40 bg-[#0A0F23]/70 backdrop-blur-sm mt-12">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <QuadricfLogo className="h-8 w-8" />
                <span className="text-lg font-semibold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">DappFexez</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                Autonomous cross‑chain remediation. Resolve. Recover. Move forward.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={handleGoToTroubleshooter} className="text-gray-300 hover:text-white">Home</button></li>
                <li><button onClick={handleGoToTroubleshooter} className="text-gray-300 hover:text-white">Troubleshooter</button></li>
                <li><button onClick={handleGoToTroubleshooter} className="text-gray-300 hover:text-white">Get Started</button></li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <h4 className="text-sm font-semibold text-white mb-3">Resources</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <button onClick={handleGoToTroubleshooter} className="text-left text-sm text-gray-300 hover:text-white truncate">
                  Troubleshooter
                </button>
                <button onClick={handleGoToTroubleshooter} className="text-left text-sm text-gray-300 hover:text-white truncate">
                  Expertise
                </button>
                <button onClick={handleGoToTroubleshooter} className="text-left text-sm text-gray-300 hover:text-white truncate">
                  NFTs
                </button>
                <button onClick={handleGoToTroubleshooter} className="text-left text-sm text-gray-300 hover:text-white truncate">
                  FAQ
                </button>
                <button onClick={handleGoToTroubleshooter} className="text-left text-sm text-gray-300 hover:text-white truncate">
                  Contact
                </button>
              </div>
            </div>
          </div>
            <div className="mt-8 pt-6 border-t border-[#3a2a06]/30 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-400">
            <span>© {new Date().getFullYear()} DappFexez. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <button onClick={handleGoToTroubleshooter} className="hover:text-white">Privacy</button>
              <button onClick={handleGoToTroubleshooter} className="hover:text-white">Terms</button>
              <button onClick={handleGoToTroubleshooter} className="hover:text-white">Support</button>
            </div>
          </div>
        </div>
        {/* No spacer: page container has padding-bottom; fixed CTA floats above */}
      </footer>

      {/* Floating CTA removed; primary CTA shown under first section */}

      {/* Subtle robot illustration on hero (decorative) */}
      <motion.svg
        aria-hidden
        viewBox="0 0 300 240"
        className="hidden md:block pointer-events-none absolute right-4 md:right-12 top-28 md:top-24 w-[220px] md:w-[300px] opacity-20"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 0.2, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <defs>
          <linearGradient id="robot-gold" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#fde68a" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#facc15" />
          </linearGradient>
        </defs>
        {/* Body */}
        <rect x="60" y="70" width="180" height="120" rx="18" fill="none" stroke="url(#robot-gold)" strokeWidth="2" />
        {/* Head */}
        <rect x="100" y="30" width="100" height="60" rx="12" fill="none" stroke="url(#robot-gold)" strokeWidth="2" />
        <circle cx="110" cy="60" r="8" fill="#0a0f23" stroke="#facc15" strokeWidth="2" />
        <circle cx="190" cy="60" r="8" fill="#0a0f23" stroke="#facc15" strokeWidth="2" />
        <rect x="130" y="46" width="40" height="8" rx="4" fill="#facc15" />
        {/* Antennas */}
        <line x1="150" y1="30" x2="150" y2="18" stroke="#f59e0b" strokeWidth="2" />
        <circle cx="150" cy="16" r="4" fill="#f59e0b" />
        {/* Arms */}
        <rect x="40" y="100" width="20" height="60" rx="8" fill="none" stroke="url(#robot-gold)" strokeWidth="2" />
        <rect x="240" y="100" width="20" height="60" rx="8" fill="none" stroke="url(#robot-gold)" strokeWidth="2" />
        {/* Panel lines */}
        <line x1="60" y1="110" x2="240" y2="110" stroke="#f59e0b" strokeOpacity="0.5" />
        <line x1="60" y1="150" x2="240" y2="150" stroke="#f59e0b" strokeOpacity="0.5" />
      </motion.svg>
    </div>
  );
};

export default HomePage;